/**
 * useExpirationNotifications Hook
 * Monitors inventory items and sends notifications for expired items
 */

import { useEffect } from 'react';
import { useSettingsStore } from '@/stores/settings-store';
import { useAuthStore } from '@/stores/auth-store';
import { apiGet } from '@/services/api-client';
import { useNotifications } from './useNotifications';

interface InventoryItem {
  id: string;
  name: string;
  expirationDate?: string;
}

export function useExpirationNotifications() {
  const { sendNotification, permissionStatus } = useNotifications();
  const { notifications: notificationSettings } = useSettingsStore();
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    // In the native mobile apps the backend sends Expo/OS push notifications.
    // Avoid also scheduling a local WebView notification for the same event.
    if (typeof window !== 'undefined' && window.ReactNativeWebView) {
      return;
    }

    if (!notificationSettings.expirationWarnings || !permissionStatus.granted || !isAuthenticated) {
      return;
    }

    // Check for expired items once per day at 9 AM
    const now = new Date();
    const nextCheck = new Date();
    nextCheck.setHours(9, 0, 0, 0);
    
    // If it's past 9 AM today, schedule for tomorrow
    if (now.getHours() >= 9) {
      nextCheck.setDate(nextCheck.getDate() + 1);
    }

    const msUntilNextCheck = nextCheck.getTime() - now.getTime();

    const checkExpirations = async () => {
      try {
        const familyId = user?.familyId;
        const endpoint = familyId
          ? `/inventory?familyId=${encodeURIComponent(familyId)}`
          : '/inventory';
        const response = await apiGet<{
          items: InventoryItem[];
        }>(endpoint);

        if (!response.success || !response.data) return;

        const items = response.data.items || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Find expired items
        const expiredItems = items.filter(item => {
          if (!item.expirationDate) return false;
          const expDate = new Date(item.expirationDate);
          expDate.setHours(0, 0, 0, 0);
          return expDate <= today;
        });

        if (expiredItems.length > 0) {
          const itemNames = expiredItems.slice(0, 3).map(item => item.name).join(', ');
          const moreText = expiredItems.length > 3 ? ` och ${expiredItems.length - 3} till` : '';

          await sendNotification({
            title: 'Utgångna föremål',
            body: `${itemNames}${moreText} har gått ut`,
            data: {
              type: 'expiration',
              count: expiredItems.length,
              url: '/app/inventory',
            },
          });
        }
      } catch (error) {
        console.error('[useExpirationNotifications] Failed to check expirations:', error);
      }
    };

    // Schedule initial check
    const timeoutId = setTimeout(() => {
      checkExpirations();
      
      // Then check daily
      const intervalId = setInterval(checkExpirations, 24 * 60 * 60 * 1000);
      
      return () => {
        clearInterval(intervalId);
      };
    }, msUntilNextCheck);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [notificationSettings.expirationWarnings, permissionStatus.granted, isAuthenticated, sendNotification, user?.familyId]);
}
